import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { TextField } from '../../../components/TextField';
import { Button } from '../../../components/Button/Button';
import { FocusPageLayout } from '../../../components/FocusPageLayout';
import { Paragraph } from '../../../components/Paragraph/Paragraph';
import { Routes } from '../../../enums/Routes';
import { useCreateTenantMutation } from '../../../hooks/mutations/useCreateTenantMutation';
import classes from './CreateTenantPage.module.css';

const PAGE_TITLE = 'Legg til ny kunde';
const BACK_LINK_LABEL = 'Tilbake til kundeoversikt';

const TENANT_NAME_LABEL = 'Kundenavn';
const TENANT_NAME_PLACEHOLDER = 'F.eks. Acme AS';
const ADMIN_EMAIL_LABEL = 'E-post til første admin';
const ADMIN_EMAIL_PLACEHOLDER = 'admin@kunde.no';
const ADMIN_FIRST_NAME_LABEL = 'Fornavn';
const ADMIN_FIRST_NAME_PLACEHOLDER = 'Ola';
const ADMIN_LAST_NAME_LABEL = 'Etternavn';
const ADMIN_LAST_NAME_PLACEHOLDER = 'Nordmann';
const ORG_NUMBER_LABEL = 'Organisasjonsnummer';
const ORG_NUMBER_PLACEHOLDER = '123456789';
const SMS_QUOTA_LABEL = 'SMS-kvote (valgfritt)';
const SMS_QUOTA_PLACEHOLDER = 'Antall SMS, eller la stå tom for ubegrenset';
const AVAILABLE_SEATS_LABEL = 'Antall lisenser (valgfritt)';
const AVAILABLE_SEATS_PLACEHOLDER = 'Antall lisenser, eller la stå tom for ubegrenset';

const CREATE_BUTTON_TEXT = 'Opprett kunde';
const CANCEL_BUTTON_TEXT = 'Avbryt';

const SUCCESS_MESSAGE = 'Kunde opprettet! Velkomst-e-post sendt til admin.';
const ERROR_MESSAGE = 'Kunne ikke opprette kunde. Prøv igjen senere.';

const createTenantSchema = z.object({
  tenantName: z.string().min(1, 'Kundenavn er påkrevd'),
  adminEmail: z
    .string()
    .min(1, 'E-post er påkrevd')
    .email('Ugyldig e-postadresse'),
  adminFirstName: z.string().min(1, 'Fornavn er påkrevd'),
  adminLastName: z.string().min(1, 'Etternavn er påkrevd'),
  orgNumber: z.string().min(1, 'Organisasjonsnummer er påkrevd'),
  smsQuota: z.string().optional(),
  availableSeats: z.string().optional(),
});

type FormData = z.infer<typeof createTenantSchema>;

const createEmptyFormData = (): FormData => ({
  tenantName: '',
  adminEmail: '',
  adminFirstName: '',
  adminLastName: '',
  orgNumber: '',
  smsQuota: '',
  availableSeats: '',
});

type FormErrors = Partial<Record<keyof FormData, string>>;

const parseOptionalNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
};

export const CreateTenantPage = (): ReactElement => {
  const navigate = useNavigate();
  const createTenantMutation = useCreateTenantMutation();

  const [formData, setFormData] = useState<FormData>(createEmptyFormData());
  const [errors, setErrors] = useState<FormErrors>({});

  const updateFormField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSmsQuotaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      updateFormField('smsQuota', value);
    }
  };

  const handleAvailableSeatsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      updateFormField('availableSeats', value);
    }
  };

  const handleCancel = () => {
    navigate(Routes.SystemAdminTenants);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (createTenantMutation.isPending) {
      return;
    }

    const result = createTenantSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors: FormErrors = {};
      result.error.errors.forEach((error) => {
        const field = error.path[0] as keyof FormData;
        fieldErrors[field] = error.message;
      });
      setErrors(fieldErrors);
      return;
    }

    createTenantMutation.mutate(
      {
        tenantName: formData.tenantName.trim(),
        adminEmail: formData.adminEmail.trim(),
        adminFirstName: formData.adminFirstName.trim(),
        adminLastName: formData.adminLastName.trim(),
        orgNumber: formData.orgNumber.trim(),
        smsQuota: parseOptionalNumber(formData.smsQuota || ''),
        availableSeats: parseOptionalNumber(formData.availableSeats || ''),
      },
      {
        onSuccess: () => {
          toast.success(SUCCESS_MESSAGE);
          navigate(Routes.SystemAdminTenants);
        },
        onError: () => {
          toast.error(ERROR_MESSAGE);
        },
      }
    );
  };

  return (
    <FocusPageLayout
      title={PAGE_TITLE}
      backLink={Routes.SystemAdminTenants}
      backLinkLabel={BACK_LINK_LABEL}
    >
      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            Kundeinformasjon
          </Paragraph>

          <TextField
            name="tenantName"
            label={TENANT_NAME_LABEL}
            value={formData.tenantName}
            onChange={(e) => updateFormField('tenantName', e.target.value)}
            placeholder={TENANT_NAME_PLACEHOLDER}
            type="text"
            required
            errorMessage={errors.tenantName}
          />

          <TextField
            name="orgNumber"
            label={ORG_NUMBER_LABEL}
            value={formData.orgNumber}
            onChange={(e) => updateFormField('orgNumber', e.target.value)}
            placeholder={ORG_NUMBER_PLACEHOLDER}
            type="text"
            required
            errorMessage={errors.orgNumber}
          />
        </div>

        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            Admin-bruker
          </Paragraph>
          <Paragraph size="sm" className={classes.sectionDescription}>
            Denne brukeren vil motta en velkomst-e-post med instruksjoner for å sette passord.
          </Paragraph>

          <TextField
            name="adminEmail"
            label={ADMIN_EMAIL_LABEL}
            value={formData.adminEmail}
            onChange={(e) => updateFormField('adminEmail', e.target.value)}
            placeholder={ADMIN_EMAIL_PLACEHOLDER}
            type="email"
            required
            errorMessage={errors.adminEmail}
          />

          <div className={classes.nameFields}>
            <TextField
              name="adminFirstName"
              label={ADMIN_FIRST_NAME_LABEL}
              value={formData.adminFirstName}
              onChange={(e) => updateFormField('adminFirstName', e.target.value)}
              placeholder={ADMIN_FIRST_NAME_PLACEHOLDER}
              type="text"
              required
              errorMessage={errors.adminFirstName}
            />

            <TextField
              name="adminLastName"
              label={ADMIN_LAST_NAME_LABEL}
              value={formData.adminLastName}
              onChange={(e) => updateFormField('adminLastName', e.target.value)}
              placeholder={ADMIN_LAST_NAME_PLACEHOLDER}
              type="text"
              required
              errorMessage={errors.adminLastName}
            />
          </div>
        </div>

        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            Abonnement
          </Paragraph>

          <TextField
            name="smsQuota"
            label={SMS_QUOTA_LABEL}
            value={formData.smsQuota}
            onChange={handleSmsQuotaChange}
            placeholder={SMS_QUOTA_PLACEHOLDER}
            type="text"
            errorMessage={errors.smsQuota}
          />

          <TextField
            name="availableSeats"
            label={AVAILABLE_SEATS_LABEL}
            value={formData.availableSeats}
            onChange={handleAvailableSeatsChange}
            placeholder={AVAILABLE_SEATS_PLACEHOLDER}
            type="text"
            errorMessage={errors.availableSeats}
          />
        </div>

        <div className={classes.actions}>
          <Button
            variant="secondary"
            size="medium"
            onClick={handleCancel}
            disabled={createTenantMutation.isPending}
            type="button"
          >
            {CANCEL_BUTTON_TEXT}
          </Button>
          <Button
            variant="primary"
            size="medium"
            type="submit"
            disabled={createTenantMutation.isPending}
          >
            {CREATE_BUTTON_TEXT}
          </Button>
        </div>
      </form>
    </FocusPageLayout>
  );
};
